import { useEffect, useState } from 'react';
import { collection, collectionGroup, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebase';

/**
 * Live case list.
 *
 * Owner mode (no facilityId): users/{userId}/cases, the pre-B2 behaviour.
 * Facility mode (facilityId set): every case stamped with that facilityId,
 * across all owners, ordered by last_modified_date desc. Filters
 * archived == false server-side; every case carries the key since #292
 * (stamped at creation, backfilled by migrate-ci, which runs before the
 * frontend deploys). Callers keep a `!archived` guard for snapshots
 * delivered mid-rollout; this hook does not filter client-side. Each case
 * carries ownerUid from its parent path so callers can build the
 * owner-scoped document path. The B1 rules require the facilityId filter;
 * the composite index (facilityId, archived, last_modified_date) in
 * firestore.indexes.json backs the query.
 */
export function useCases(userId, { facilityId = null } = {}) {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(Boolean(userId || facilityId));
    const [error, setError] = useState(null);

    useEffect(() => {
        const ref = facilityId
            ? query(
                collectionGroup(db, 'cases'),
                where('facilityId', '==', facilityId),
                where('archived', '==', false),
                orderBy('last_modified_date', 'desc'),
            )
            : userId ? collection(db, 'users', userId, 'cases') : null;
        if (!ref) {
            setLoading(false);
            setCases([]);
            return undefined;
        }
        setLoading(true);
        setError(null);
        const unsubscribe = onSnapshot(
            ref,
            (snapshot) => {
                const next = [];
                snapshot.forEach((doc) => {
                    // Facility mode: a case we cannot attribute must not look like ours.
                    const ownerUid = doc.ref?.parent?.parent?.id ?? (facilityId ? null : userId);
                    next.push({ id: doc.id, ownerUid, ...doc.data() });
                });
                setCases(next);
                setLoading(false);
            },
            (err) => {
                setError(err);
                setLoading(false);
            },
        );
        return unsubscribe;
    }, [userId, facilityId]);

    return { cases, loading, error };
}
